import Button from '@orn/shared-web/components/Button/Button';
import * as React from 'react';

interface IOnboardingStepActionsProps {
  /** Primary button label. Defaults to "Continue". */
  continueLabel?: React.ReactNode;
  /** Primary button `type` — use "submit" for steps that submit a surrounding form. Defaults to "button". */
  continueType?: 'button' | 'submit';
  /** Click handler for the primary button. Omit for `type="submit"` forms that handle submission themselves. */
  onContinue?: () => void;
  isContinueDisabled?: boolean;
  isContinueLoading?: boolean;
  /** When provided, renders a "Skip for now" link beneath the primary button. */
  onSkip?: () => void;
  isSkipDisabled?: boolean;
  /** Skip link label. Defaults to "Skip for now". */
  skipLabel?: React.ReactNode;
}

/**
 * Shared footer actions for onboarding steps — a full-width primary button with
 * an optional "Skip for now" link beneath it. Centralizes the button variants
 * (`quaternary` primary, `link-gray` skip) so the skip affordance stays
 * consistent across every step and can't drift.
 */
const OnboardingStepActions = ({
  continueLabel = 'Continue',
  continueType = 'button',
  onContinue,
  isContinueDisabled,
  isContinueLoading,
  onSkip,
  isSkipDisabled,
  skipLabel = 'Skip for now',
}: IOnboardingStepActionsProps) => (
  <div className="flex w-full flex-col gap-5">
    <Button
      type={continueType}
      size="xl"
      color="quaternary"
      className="w-full"
      isDisabled={isContinueDisabled}
      isLoading={isContinueLoading}
      onClick={onContinue}
    >
      {continueLabel}
    </Button>
    {onSkip && (
      <Button
        type="button"
        size="md"
        color="link-gray"
        className="self-center"
        isDisabled={isSkipDisabled}
        onClick={onSkip}
      >
        {skipLabel}
      </Button>
    )}
  </div>
);

OnboardingStepActions.displayName = 'OnboardingStepActions';

export default OnboardingStepActions;
